import * as React from "react"
import { Avatar, AvatarFallback, AvatarImage, cn, getAvatarColor } from "@nexus/ui"
import { personaInitials } from "../../lib/persona-format"
import { isPortraitUrlCached, markPortraitUrlLoaded } from "../../lib/persona-portrait-cache"

const AVATAR_FALLBACK_TEXT_COLOR = "hsl(0, 0%, 25%)"

type PortraitLoadStatus = "idle" | "loading" | "loaded" | "error"

export interface PersonaPortraitProps {
  avatarUrl: string | null
  personalityKey: string
  title: string
  className?: string
  fallbackClassName?: string
  /** Defaults to lazy — table rows below the fold should not eagerly fetch every portrait. */
  loading?: "lazy" | "eager"
}

function initialPortraitLoadStatus(avatarUrl: string | null): PortraitLoadStatus {
  if (!avatarUrl) return "idle"
  if (isPortraitUrlCached(avatarUrl)) return "loaded"
  return "loading"
}

/**
 * Persona portrait with a crossfade from hashed initials → loaded image.
 * Radix swaps fallback/image instantly; we layer both and animate opacity so
 * the reveal feels intentional rather than a hard cut.
 */
export function PersonaPortrait({
  avatarUrl,
  personalityKey,
  title,
  className,
  fallbackClassName,
  loading = "lazy",
}: PersonaPortraitProps) {
  const imgRef = React.useRef<HTMLImageElement>(null)
  const [loadStatus, setLoadStatus] = React.useState<PortraitLoadStatus>(() =>
    initialPortraitLoadStatus(avatarUrl),
  )

  React.useEffect(() => {
    if (!avatarUrl) {
      setLoadStatus("idle")
      return
    }
    if (isPortraitUrlCached(avatarUrl)) {
      setLoadStatus("loaded")
      return
    }
    setLoadStatus("loading")
  }, [avatarUrl])

  // Warm images can finish before React attaches onLoad — settle from the element.
  React.useEffect(() => {
    if (!avatarUrl || loadStatus === "loaded" || loadStatus === "error") return
    const img = imgRef.current
    if (!img?.complete) return
    if (img.naturalWidth === 0) {
      setLoadStatus("error")
      return
    }
    markPortraitUrlLoaded(avatarUrl)
    setLoadStatus("loaded")
  }, [avatarUrl, loadStatus])

  const showPortrait = loadStatus === "loaded"
  const isPortraitLoading =
    Boolean(avatarUrl) && loadStatus !== "loaded" && loadStatus !== "error"
  const fallbackColor = getAvatarColor(personalityKey)
  const initials = personaInitials(title)

  const handlePortraitLoad = React.useCallback(() => {
    if (avatarUrl) markPortraitUrlLoaded(avatarUrl)
    setLoadStatus("loaded")
  }, [avatarUrl])

  const handlePortraitError = React.useCallback(() => {
    setLoadStatus("error")
  }, [])

  return (
    <Avatar className={cn("relative overflow-hidden", className)}>
      {avatarUrl ? (
        <>
          <span
            aria-hidden={showPortrait}
            className={cn(
              "absolute inset-0 flex items-center justify-center rounded-full transition-opacity duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
              showPortrait ? "pointer-events-none opacity-0" : "opacity-100",
              fallbackClassName,
            )}
            style={{
              backgroundColor: fallbackColor,
              color: AVATAR_FALLBACK_TEXT_COLOR,
            }}
          >
            {initials}
            {isPortraitLoading && (
              <span
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-full motion-reduce:hidden"
                aria-hidden
              >
                <span
                  className="absolute inset-y-0 -left-1/2 w-1/2 animate-[osc-shimmer-sweep_2.4s_ease-in-out_infinite]"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.38) 50%, transparent 100%)",
                  }}
                />
              </span>
            )}
          </span>
          <AvatarImage
            ref={imgRef}
            src={avatarUrl}
            alt={title}
            loading={loading}
            decoding="async"
            onLoad={handlePortraitLoad}
            onError={handlePortraitError}
            className={cn(
              "relative z-[1] object-cover transition-opacity duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
              showPortrait ? "opacity-100" : "opacity-0",
            )}
          />
        </>
      ) : (
        <AvatarFallback
          className={fallbackClassName}
          style={{
            backgroundColor: fallbackColor,
            color: AVATAR_FALLBACK_TEXT_COLOR,
          }}
        >
          {initials}
        </AvatarFallback>
      )}
    </Avatar>
  )
}
